/**
 * Banner — info/warning callout with icon, title, and message.
 *
 * The icon is accepted as a ReactNode slot rather than importing a specific
 * icon library, keeping the component decoupled from the app's SVG icons.
 * When no icon is provided, a default slot renders nothing.
 *
 * Used in the alarm control panel for "Schedule Override" info display.
 */
import { memo, type FC, type ReactNode } from "react";
import "../../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { StyleProp, ViewStyle } from "react-native";
import { View } from "../View";
import { Text, type TextSize } from "../Text";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type BannerVariant = "warning" | "info";

export interface BannerProps {
  readonly title?: string;
  readonly message?: string;
  readonly variant?: BannerVariant;
  readonly textTitleStyle?: TextSize;
  readonly textMessageStyle?: TextSize;
  readonly containerStyle?: StyleProp<ViewStyle>;
  /** Style merged onto the icon wrapper View, allowing consumers to override marginTop etc. */
  readonly iconStyle?: StyleProp<ViewStyle>;
  /** Custom icon node. When omitted, no icon is rendered. */
  readonly icon?: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Banner: FC<BannerProps> = memo(
  ({
    title,
    message,
    variant = "warning",
    textTitleStyle = "xs/semibold",
    textMessageStyle = "xxs/regular",
    containerStyle,
    iconStyle,
    icon,
  }) => {
    const { theme } = useUnistyles();

    const accentColor =
      variant === "info" ? theme.colors.interactive.buttons.secondary_foreground : theme.colors.status.warning;

    const textColor = variant === "info" ? theme.colors.text : theme.colors.warning[900];

    const background = variant === "info" ? theme.colors.surfaceCardInner : theme.colors.surfaceCardInnerWarning;

    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: background,
            borderColor: accentColor,
          },
          containerStyle,
        ]}
      >
        <View flexDirection="row" gap="md">
          {icon !== undefined && <View style={[styles.icon, iconStyle]}>{icon}</View>}
          <View gap="xs" style={styles.textContainer}>
            {!!title && (
              <Text textStyle={textTitleStyle} style={{ color: textColor }}>
                {title}
              </Text>
            )}
            {message && (
              <Text textStyle={textMessageStyle} style={{ color: textColor }}>
                {message}
              </Text>
            )}
          </View>
        </View>
      </View>
    );
  },
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    padding: theme.spacing.lg,
    paddingRight: theme.spacing["3xl"],
    borderRadius: theme.spacing.xs,
    borderWidth: 1,
  },
  icon: {
    marginTop: theme.spacing.xxs,
  },
  textContainer: {
    flex: 1,
    paddingRight: theme.spacing["3xl"],
    justifyContent: "center",
  },
}));
